// The knitmap image (scripts/build-knit-along.py) is one pixel per cell, first course at the
// bottom as a knitting program shows it: knitting cells in their yarn's colour, needles holding
// a loop grey, idle needles clear.

const images = new Map<string, Promise<HTMLImageElement>>();

export const loadKnitmap = (src: string): Promise<HTMLImageElement> => {
  let pending = images.get(src);
  if (!pending) {
    pending = new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`${src} did not load`));
      image.src = src;
    });
    pending.catch(() => images.delete(src));
    images.set(src, pending);
  }
  return pending;
};

type StripFrame = {
  readonly width: number;
  readonly height: number;
  readonly progress: number;
  // Opacity of the courses still to come.
  readonly ahead: number;
  readonly playhead: string;
};

// The whole knitmap in the strip: knitted courses in full, the rest faded, and a playhead on the
// course on the needles.
export const drawKnitmapStrip = (
  context: CanvasRenderingContext2D,
  knitmap: HTMLImageElement,
  { width, height, progress, ahead, playhead }: StripFrame,
) => {
  const needles = knitmap.naturalWidth;
  const courses = knitmap.naturalHeight;
  const cellHeight = height / courses;
  const knitted = Math.min(Math.floor(progress), courses);

  context.clearRect(0, 0, width, height);
  context.imageSmoothingEnabled = width < needles || height < courses;
  context.globalAlpha = ahead;
  context.drawImage(knitmap, 0, 0, width, height);
  context.globalAlpha = 1;
  if (knitted > 0) {
    const top = height - knitted * cellHeight;
    context.drawImage(knitmap, 0, courses - knitted, needles, knitted, 0, top, width, height - top);
  }
  if (progress >= courses) return;
  context.fillStyle = playhead;
  context.fillRect(0, Math.round(height - (Math.floor(progress) + 0.5) * cellHeight - 1), width, 2);
};

// A stretch of courses at a whole number of pixels per cell, for close-ups of shaping.
export const drawKnitmapCourses = (
  context: CanvasRenderingContext2D,
  knitmap: HTMLImageElement,
  { from, to, cell }: { readonly from: number; readonly to: number; readonly cell: number },
) => {
  const needles = knitmap.naturalWidth;
  const courses = knitmap.naturalHeight;
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, needles * cell, (to - from) * cell);
  context.drawImage(knitmap, 0, courses - to, needles, to - from, 0, 0, needles * cell, (to - from) * cell);
};
